import { z } from 'zod';
import { Instant } from './primitives.js';
/**
 * What a person has agreed to, and what they are still to agree to (decisions 5.46 and
 * 5.47, 2026-09-28).
 *
 * The four documents are rows in `legal_documents`, one row per published version, and
 * every agreement is a row in `consents` against one of those rows. So "has she agreed to
 * the current terms" is a fact the server can answer, and the phone asks her again when
 * the answer is no — but only for a document she agreed to an earlier version of. Somebody
 * who never agreed to a document is not asked here: the terms and the privacy policy are
 * agreed to at sign-up, the professional terms when a professional joins, and the health
 * notice before the first health details are saved.
 */
/**
 * The minimum age (client, 2026-09-28, replacing decision 4.2's "no minimum age"): High
 * Impact is for people aged 13 and over, as the privacy policy and the terms say. Sign-up
 * asks her to confirm it (`SignUpBody.age_confirmed`), and a date of birth that makes her
 * younger is refused (`PATCH /v1/app/me`, `details.reason: 'under_minimum_age'`). One
 * number, read by the server and the phone alike.
 */
export const MINIMUM_AGE = 13;
/**
 * Whole years from a `YYYY-MM-DD` date of birth to a `YYYY-MM-DD` today — birthdays
 * counted by the calendar, not by 365.25 days, so she is 13 on her thirteenth birthday
 * and not a few hours either side of it. Null for something that is not a date.
 */
export function wholeYears(dateOfBirth, today) {
    const [by, bm, bd] = dateOfBirth.split('-').map(Number);
    const [ty, tm, td] = today.split('-').map(Number);
    if (!by || !bm || !bd || !ty || !tm || !td)
        return null;
    return ty - by - (tm < bm || (tm === bm && td < bd) ? 1 : 0);
}
export const LegalKind = z.enum(['terms', 'privacy', 'health_notice', 'provider_terms']);
export const LegalToAccept = z.object({
    kind: LegalKind,
    /** The version in force, which is the one to send back when accepting. */
    version: z.string(),
    published_at: Instant,
    /** What changed, in the desk's words. Null when the desk wrote nothing. */
    summary: z.string().nullable(),
    /** The newest version of this document she agreed to before. */
    accepted_version: z.string(),
});
export const LegalStatus = z.object({
    /**
     * Newer versions of documents she agreed to before, oldest kind first (terms, privacy,
     * health notice, professional terms). Empty when nothing is owed. The professional
     * terms appear only for an account with the professional role.
     */
    to_accept: z.array(LegalToAccept),
    /**
     * Whether she has agreed to the health notice — any version. The phone shows the
     * notice before the first health details are saved while this is false. An older
     * version agreed to is `true` here and appears in `to_accept`.
     */
    health_notice_accepted: z.boolean(),
    /** The health notice in force, or null while none has been published. */
    health_notice_version: z.string().nullable(),
});
export const AcceptLegalBody = z.object({
    /** One or more documents, each by the version that was shown. */
    documents: z
        .array(z.object({ kind: LegalKind, version: z.string().min(1).max(40) }))
        .min(1)
        .max(4),
});
//# sourceMappingURL=legal.js.map